import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cn } from "@volca/ui/lib/utils"
import {
  COLOR_TREATMENTS,
  type ColorTreatment,
  type ComponentColor,
} from "@volca/ui/tokens/component-colors"

const BASE =
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-focus focus-visible:ring-[3px] focus-visible:ring-focus/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-danger-line aria-invalid:ring-danger/20 [&>svg]:pointer-events-none [&>svg]:size-3! [&_[data-slot=spinner]]:size-3 [&_[data-slot=spinner]_svg]:size-3!"

const VARIANTS = {
  default: { treatment: "bold", defaultColor: "brand", classes: "" },
  secondary: { treatment: "subtle", defaultColor: "neutral", classes: "" },
  outline: {
    treatment: "outline",
    defaultColor: "neutral",
    classes:
      "[a]:hover:bg-neutral-subtle-hovered [a]:active:bg-neutral-subtle-pressed",
  },
  ghost: {
    treatment: "text",
    defaultColor: "neutral",
    classes: "hover:bg-neutral-subtle-hovered active:bg-neutral-subtle-pressed",
  },
  link: {
    treatment: "text",
    defaultColor: "brand",
    classes: "underline-offset-4 hover:underline",
  },
} as const satisfies Record<
  string,
  {
    treatment: keyof ColorTreatment
    defaultColor: ComponentColor
    classes: string
  }
>

type BadgeVariant = keyof typeof VARIANTS
type BadgeColor = ComponentColor

interface BadgeVariantProps {
  variant?: BadgeVariant | null
  color?: BadgeColor | null
  className?: string
}

const badgeVariants = ({
  variant,
  color,
  className,
}: BadgeVariantProps = {}) => {
  const recipe = VARIANTS[variant ?? "default"]
  return cn(
    BASE,
    COLOR_TREATMENTS[color ?? recipe.defaultColor][recipe.treatment],
    recipe.classes,
    className
  )
}

function Badge({
  className,
  variant = "default",
  color,
  render,
  ...props
}: Omit<useRender.ComponentProps<"span">, "color"> &
  Omit<BadgeVariantProps, "className">) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: badgeVariants({ variant, color, className }),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants, type BadgeVariant, type BadgeColor }
